import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { feeAPI } from '../../services/api';
import { useNotification } from '../../hooks/useNotification';

const FeeList = () => {
  const { t, i18n } = useTranslation();
  const { showError } = useNotification();
  const [fees, setFees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    loadFees();
  }, [typeFilter, statusFilter]);

  const loadFees = async () => {
    try {
      const params = {};
      if (typeFilter) params.type = typeFilter;
      if (statusFilter) params.is_active = statusFilter;

      const response = await feeAPI.getAll(params);
      setFees(response.data.data || response.data);
    } catch (error) {
      showError(t('messages.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('fees.deleteConfirm'))) return;
    try {
      await feeAPI.delete(id);
      loadFees();
    } catch (error) {
      showError(t('messages.error'));
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'delivery': return { bg: 'bg-blue-500', text: 'text-blue-700' };
      case 'service': return { bg: 'bg-green-500', text: 'text-green-700' };
      case 'packaging': return { bg: 'bg-purple-500', text: 'text-purple-700' };
      case 'other': return { bg: 'bg-gray-500', text: 'text-gray-700' };
      default: return { bg: 'bg-gray-400', text: 'text-gray-700' };
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{t('fees.title')}</h2>
          <p className="text-gray-600 mt-1">{t('fees.subtitle')}</p>
        </div>
        <Link
          to="/fees/new"
          className="flex items-center px-4 py-2 bg-gradient-to-r bg-gray-900 text-white rounded-lg hover:bg-yellow-500  transition-all duration-200 shadow-lg hover:shadow-xl"
        >
          <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          {t('fees.addFeeButton')}
        </Link>
      </div>

      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="mb-6 flex gap-4">
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">{t('fees.allTypes')}</option>
            <option value="delivery">{t('fees.delivery')}</option>
            <option value="service">{t('fees.service')}</option>
            <option value="packaging">{t('fees.packaging')}</option>
            <option value="other">{t('fees.other')}</option>
          </select>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">{t('fees.allStatus')}</option>
            <option value="1">{t('fees.active')}</option>
            <option value="0">{t('fees.inactive')}</option>
          </select>
        </div>

        {/* Modern Card Layout */}
        <div className="space-y-3">
          {/* Table Header */}
          <div className="grid grid-cols-12 gap-4 px-6 py-3 bg-gray-50 rounded-lg text-xs font-medium text-gray-500 uppercase tracking-wider">
            <div className="col-span-1">{t('fees.status')}</div>
            <div className="col-span-3">{t('fees.name')}</div>
            <div className="col-span-2">{t('fees.type')}</div>
            <div className="col-span-2">{t('fees.amount')}</div>
            <div className="col-span-3">{t('fees.description')}</div>
            <div className="col-span-1 text-right">{t('fees.actions')}</div>
          </div>

          {/* Card Items */}
          {fees.length === 0 ? (
            <div className="px-6 py-8 text-center text-gray-500">
              {t('fees.noFees')}
            </div>
          ) : (
            fees.map((fee) => {
              const typeColor = getTypeColor(fee.type);
              return (
                <div key={fee.id} className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 border border-gray-100">
                  <div className="grid grid-cols-12 gap-4 px-6 py-4 items-center">
                    {/* Status */}
                    <div className="col-span-1 flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-full ${fee.is_active ? 'bg-green-500' : 'bg-red-500'}`}></div>
                    </div>

                    {/* Name */}
                    <div className="col-span-3">
                      <div className="text-sm font-semibold text-gray-900">
                        {i18n.language === 'ar' && fee.name_ar ? fee.name_ar : fee.name}
                      </div>
                      {fee.name_ar && i18n.language !== 'ar' && (
                        <div className="text-xs text-gray-500 mt-0.5">{fee.name_ar}</div>
                      )}
                    </div>

                    {/* Type */}
                    <div className="col-span-2 flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-full ${typeColor.bg}`}></div>
                      <span className={`text-sm font-medium capitalize ${typeColor.text}`}>
                        {fee.type}
                      </span>
                    </div>

                    {/* Amount */}
                    <div className="col-span-2">
                      <div className="text-sm font-semibold text-green-600">
                        {t('common.currency')} {parseFloat(fee.amount).toFixed(2)}
                      </div>
                    </div>

                    {/* Description */}
                    <div className="col-span-3">
                      <div className="text-sm text-gray-600 truncate">
                        {fee.description || '-'}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="col-span-1 flex items-center justify-end gap-2">
                      <Link
                        to={`/fees/${fee.id}`}
                        className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Edit"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </Link>
                      <button
                        onClick={() => handleDelete(fee.id)}
                        className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default FeeList;
